// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@heroui/react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { type ModalProps } from '@/stores/use-modal-store.ts';
import { API_BASE, S3_ENDPOINT_BUCKET } from '@/config';
import React, { useState } from 'react';
import type { File as S3File } from '@/models/file';
import { UploadFilesS3PresignedUrl } from '../files/upload-files-s3-presigned-url';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '@/context/socket-context/use-external-socket.ts';

const formSchema = z.object({
  name: z.string().min(1, {
    message: 'Server name is required.',
  }),
  imageUrl: z.string().optional(),
  isPrivate: z.boolean(),
});

export const CreateServerModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  data,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [fileUrl, setFileUrl] = useState('');
  const socket = useSocket();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      imageUrl: '',
      isPrivate: false,
    },
  });

  const isLoading = form.formState.isSubmitting;

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    values = { ...values, imageUrl: fileUrl };
    try {
      const createdServer = await fetch(`${API_BASE}/api/servers`, {
        method: 'POST',
        body: JSON.stringify(values),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await createdServer.json();
      const server = data;

      if (socket?.readyState !== WebSocket.OPEN) {
        return;
      }
      socket.send(
        JSON.stringify({
          type: 'server-add',
          payload: server,
        })
      );

      form.reset();
      navigate(`/servers/${server?.id}`);
      onClose();
    } catch {
      // TODO: translate
      toast.error('Uh oh! Something went wrong.');
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const onSubmitImage = async (savedFiles: S3File[]) => {
    const fileUrl = S3_ENDPOINT_BUCKET + '/' + savedFiles[0].fileName;
    setFileUrl(fileUrl);
    form.setValue('imageUrl', fileUrl);
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={handleClose}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <div className="text-center text-2xl font-bold">
                {t('CreateServerModal.title')}
              </div>
              <div className="text-center text-sm text-muted-foreground">
                {t('CreateServerModal.description')}
              </div>
            </ModalHeader>
            <ModalBody>
              <div className="flex items-center justify-center text-center px-6">
                <UploadFilesS3PresignedUrl
                  onUploadSuccess={() => { }}
                  onFilesSaved={onSubmitImage}
                  folderName="serverImage"
                />
              </div>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                >
                  <div className="space-y-2 px-6">
                    <div className="flex items-center justify-center text-center">
                      <FormField
                        control={form.control}
                        name="imageUrl"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              {field.value &&
                                <img src={field.value} width={128} />
                              }
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-bold uppercase">
                            {t('CreateServerModal.serverName')}
                          </FormLabel>
                          <FormControl>
                            <Input
                              disabled={isLoading}
                              placeholder={t(
                                'CreateServerModal.serverNamePlaceholder'
                              )}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="isPrivate"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-start gap-2">
                          <FormLabel className="text-xs font-bold uppercase">
                            {t('CreateServerModal.private')}
                          </FormLabel>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <ModalFooter>
                    <Button
                      color="primary"
                      disabled={isLoading}
                      type="submit"
                      variant="primary"
                    >
                      {t('CreateServerModal.create')}
                    </Button>
                  </ModalFooter>
                </form>
              </Form>
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
